import { z } from "zod";
/**
 * Escalation ladder shape (§4.2). Intervals are configurable per profile; the
 * anchor (T₀) comes from the wake-time solver. The ladder lives *in the payload*
 * so the worker can run it offline without calling home; the runtime state
 * machine that walks it lives in the worker.
 *
 *   WAKE       call #1
 *   +3m        call #2
 *   +8m        call #3 + SMS
 *   +15m       call #4, tone firmer
 *   HARD WAKE  call every 2m, never auto-stops, must be dismissed
 */
/** A single rung below Hard Wake. `offsetMin` is minutes after T₀. */
export const EscalationStep = z.object({
    offsetMin: z.number().int().min(0),
    /** Also fire an SMS nudge at this rung. */
    sms: z.boolean().default(false),
    /** Shift the agent's tone firmer from this rung onward. */
    toneFirmer: z.boolean().default(false),
});
export const HardWakeBehavior = z.object({
    /** Re-call cadence once Hard Wake is reached. */
    intervalMin: z.number().int().min(1).default(2),
    /** Hard Wake never auto-stops; it must be dismissed. Pinned true. */
    neverAutoStops: z.literal(true).default(true),
});
export const ReverifyConfig = z.object({
    /** Minutes after a successful dismissal to call back once. Spec: 5–10m. */
    delayMinMin: z.number().int().min(1).default(5),
    delayMaxMin: z.number().int().min(1).default(10),
});
export const EscalationLadder = z.object({
    /** Rungs below Hard Wake, ascending by offset. step[0] is T₀ itself (call #1). */
    steps: z.array(EscalationStep).min(1),
    hardWake: HardWakeBehavior,
    reverify: ReverifyConfig,
});
/** The v1 ladder, held in v2. Offsets in minutes after T₀. */
export const DEFAULT_LADDER = {
    steps: [
        { offsetMin: 0, sms: false, toneFirmer: false }, // call #1 at WAKE
        { offsetMin: 3, sms: false, toneFirmer: false }, // call #2
        { offsetMin: 8, sms: true, toneFirmer: false }, // call #3 + SMS
        { offsetMin: 15, sms: false, toneFirmer: true }, // call #4, firmer
    ],
    hardWake: { intervalMin: 2, neverAutoStops: true },
    reverify: { delayMinMin: 5, delayMaxMin: 10 },
};
//# sourceMappingURL=escalation.js.map